import type { ExpresionAvatar } from '@alter/types';
import type { ExpresionConfig } from './types.js';

/**
 * Mapa de las 10 expresiones de `expresion_vr` (JC §3.1, enum rediseñado
 * por el core el 2026-08-26) a sus parámetros de rig. Cada entrada es un
 * `ExpresionConfig`: los campos ausentes caen a `NEUTRAL_PARAMS` al
 * resolver en el motor.
 *
 * Tipado como `Record<ExpresionAvatar, ExpresionConfig>`: agregar una
 * expresión al enum en `@alter/types` rompe la compilación aquí hasta
 * agregar su config.
 *
 * **Procedencia (tanda 1, 2026-09-19).** El enum anterior del cliente (9
 * valores, contrato v1.3) no existía en el wire vigente. Las configs
 * calibradas en F0.6 se reasignaron así:
 *
 * | Expresión      | Config                                  |
 * |----------------|-----------------------------------------|
 * | `NEUTRAL`      | sin cambio                              |
 * | `DOLOR`        | sin cambio                              |
 * | `ALEGRIA`      | la de `SONRIENTE`                       |
 * | `ENOJO`        | la de `MOLESTO`                         |
 * | `SORPRESA`     | la de `SORPRESA_NORMAL`                 |
 * | `TRISTEZA`     | la de `TRISTE`                          |
 * | `AVERSION`     | PROVISIONAL — sin calibrar (tanda 3)    |
 * | `ESPERANZA`    | PROVISIONAL — sin calibrar (tanda 3)    |
 * | `MIEDO`        | PROVISIONAL — sin calibrar (tanda 3)    |
 * | `PREOCUPACION` | PROVISIONAL — sin calibrar (tanda 3)    |
 *
 * Las provisionales usan los 11 `RigParams` actuales sin ampliar el
 * contrato. El criterio es que no contradigan la emoción, no que se
 * distingan finamente entre sí. Dos límites conocidos del contrato de
 * parámetros, para la calibración: `MIEDO` pide ojo muy abierto y
 * `parpado` no tiene margen por encima del neutro; `AVERSION` pide
 * `noseSneer`, que hoy solo se activa como mueca de `DOLOR` (tensión > 0.8).
 *
 * Criterios de la calibración de F0.6 que siguen vigentes:
 *
 * 1. **`intensidad` sale de las configs.** Es el canal que modula desde
 *    afuera (páginas dev y, a futuro, `expresion_detalle`); cifrar en él la
 *    identidad de una expresión la vuelve otra con cualquier override. La
 *    amplitud vive en los parámetros; `intensidad` solo modula.
 * 2. **`parpado` no baja de 1.0 para abrir los ojos.** El neutro es el
 *    techo (`07_BLENDSHAPES_ARKIT.md` §4); pedir menos cierra los ojos.
 * 3. **La ceja triste es `ceja_interna`**, no `ceja_*` negativo: bajar la
 *    ceja externa activa `browDown`, el músculo del enojo.
 */
export const EXPRESIONES_CONFIG: Record<ExpresionAvatar, ExpresionConfig> = {
  NEUTRAL: {},
  ALEGRIA: {
    comisura_izq: 0.55,
    comisura_der: 0.55,
  },
  // PROVISIONAL — sin calibrar (tanda 3). Rechazo: boca hacia abajo, ceja
  // baja sin llegar a la mueca de DOLOR, y la mirada que se aparta.
  AVERSION: {
    ceja_izq: -0.3,
    ceja_der: -0.3,
    comisura_izq: -0.6,
    comisura_der: -0.6,
    tension_mandib: 0.45,
    mirada_x: -0.35,
  },
  DOLOR: {
    tension_mandib: 0.95,
    ceja_izq: -0.8,
    ceja_der: -0.8,
  },
  ENOJO: {
    ceja_izq: -0.45,
    ceja_der: -0.45,
    tension_mandib: 0.4,
  },
  // PROVISIONAL — sin calibrar (tanda 3). Positiva y contenida: sonrisa
  // leve con el centro de la ceja apenas elevado.
  ESPERANZA: {
    ceja_interna: 0.3,
    comisura_izq: 0.3,
    comisura_der: 0.3,
    mirada_y: 0.1,
  },
  // PROVISIONAL — sin calibrar (tanda 3). Ceja completa alzada y juntada
  // al centro, boca tensa hacia abajo. Sin ojo muy abierto: `parpado` no
  // tiene margen sobre el neutro.
  MIEDO: {
    ceja_izq: 0.5,
    ceja_der: 0.5,
    ceja_interna: 0.9,
    comisura_izq: -0.25,
    comisura_der: -0.25,
  },
  // PROVISIONAL — sin calibrar (tanda 3). Ansiedad sostenida: ceja triste
  // moderada con algo de tensión, más leve que TRISTEZA.
  PREOCUPACION: {
    ceja_interna: 0.55,
    comisura_izq: -0.3,
    comisura_der: -0.3,
    tension_mandib: 0.35,
  },
  SORPRESA: {
    ceja_izq: 0.6,
    ceja_der: 0.6,
    ceja_interna: 0.6,
  },
  // `ceja_*` queda en neutro a propósito: la ceja triste es sólo el
  // extremo interno. Bajarla sería enojo.
  TRISTEZA: {
    ceja_interna: 0.7,
    comisura_izq: -0.75,
    comisura_der: -0.75,
    mirada_y: -0.3,
    parpado_izq: 0.85,
    parpado_der: 0.85,
  },
};

/**
 * Configs calibradas en F0.6 que **salieron del enum** con el contrato
 * vigente y no heredó ninguna expresión. Fuera del tipo del enum a
 * propósito: el motor no las resuelve.
 *
 * Se conservan como insumo del vocabulario futuro de `expresion_detalle`
 * (ítem 61 del core), donde la intensidad y los matices vuelven como
 * claves propias. No agregar entradas: es un archivo histórico, no un
 * catálogo.
 */
export const EXPRESIONES_CONFIG_RETIRADAS: Readonly<Record<string, ExpresionConfig>> = {
  /** Alegría plena. `ALEGRIA` heredó la de `SONRIENTE`, más contenida. */
  ALEGRE: {
    comisura_izq: 0.95,
    comisura_der: 0.95,
    parpado_izq: 0.8,
    parpado_der: 0.8,
  },
  /** Enojo marcado. `ENOJO` heredó la de `MOLESTO`, más contenida. */
  ENOJADO: {
    ceja_izq: -0.8,
    ceja_der: -0.8,
    tension_mandib: 0.75,
    comisura_izq: -0.45,
    comisura_der: -0.45,
  },
  /** Sorpresa intensa. El enum vigente no distingue intensidad de sorpresa. */
  SORPRESA_ALTA: {
    ceja_izq: 1.0,
    ceja_der: 1.0,
    ceja_interna: 1.0,
    parpado_izq: 1.0,
    parpado_der: 1.0,
    tension_mandib: 0.3,
  },
};
